// Offline Cache Service
// localStorage-based data caching for offline mode
// Key structure: workvan_data_${table}_${companyId}

const OFFLINE_MODE_KEY = 'workvan_offline_mode';
const CACHE_PREFIX = 'workvan_data_';

// --- Toggle ---

export function isOfflineModeEnabled() {
  return localStorage.getItem(OFFLINE_MODE_KEY) === 'true';
}

export function setOfflineModeEnabled(enabled) {
  if (enabled) {
    localStorage.setItem(OFFLINE_MODE_KEY, 'true');
  } else {
    localStorage.removeItem(OFFLINE_MODE_KEY);
  }
}

// --- Read / Write ---

export function cacheData(table, companyId, data) {
  try {
    const key = `${CACHE_PREFIX}${table}_${companyId}`;
    const payload = JSON.stringify({ data, cachedAt: new Date().toISOString() });
    localStorage.setItem(key, payload);
  } catch (e) {
    // localStorage full or unavailable
    console.warn('offlineCache: failed to cache', table, e.message);
  }
}

export function getCachedData(table, companyId) {
  try {
    const key = `${CACHE_PREFIX}${table}_${companyId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.data || null;
  } catch (e) {
    return null;
  }
}

export function getCacheAge(table, companyId) {
  try {
    const key = `${CACHE_PREFIX}${table}_${companyId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.cachedAt || null;
  } catch (e) {
    return null;
  }
}

// --- Warm Cache (bulk download) ---

const CACHE_TABLES = [
  {
    table: 'customers',
    query: (sb, companyId) => sb.from('customers').select('*').eq('company_id', companyId)
  },
  {
    table: 'jobs',
    query: (sb, companyId) => sb.from('jobs').select('*, customers(id, first_name, last_name, name, phone, email, address, city, state, zip)').eq('company_id', companyId)
  },
  {
    table: 'quotes',
    query: (sb, companyId) => sb.from('quotes').select('*, customers(id, first_name, last_name, name, phone, email, address, city, state, zip)').eq('company_id', companyId)
  },
  {
    table: 'skus',
    query: (sb, companyId) => sb.from('skus').select('*').eq('company_id', companyId).eq('is_active', true)
  },
  {
    table: 'user_companies',
    query: (sb, companyId) => sb.from('user_companies').select('*').eq('company_id', companyId)
  },
  {
    table: 'pricing_tables',
    query: (sb, companyId) => sb.from('pricing_tables').select('*').eq('company_id', companyId)
  },
  {
    table: 'invoices',
    query: (sb, companyId) => sb.from('invoices').select('*').eq('company_id', companyId)
  },
  {
    table: 'vendors',
    query: (sb, companyId) => sb.from('vendors').select('*').eq('company_id', companyId)
  },
  {
    table: 'leads',
    query: (sb, companyId) => sb.from('leads').select('*').eq('company_id', companyId)
  }
];

export async function warmCache(companyId, supabaseClient, onProgress) {
  const results = await Promise.allSettled(
    CACHE_TABLES.map(async ({ table, query }, index) => {
      const { data, error } = await query(supabaseClient, companyId);
      if (error) throw error;
      cacheData(table, companyId, data || []);
      if (onProgress) onProgress(index + 1, CACHE_TABLES.length, table);
      return { table, count: (data || []).length };
    })
  );
  return results;
}

// --- Clear ---

export function clearCache() {
  const keysToRemove = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => localStorage.removeItem(key));
}

// --- Info ---

export function getCacheSize() {
  let totalBytes = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(CACHE_PREFIX)) {
      const value = localStorage.getItem(key);
      totalBytes += (key.length + (value ? value.length : 0)) * 2; // UTF-16
    }
  }
  return (totalBytes / (1024 * 1024)).toFixed(2); // MB
}

export function getCacheSummary(companyId) {
  const summary = {};
  CACHE_TABLES.forEach(({ table }) => {
    const data = getCachedData(table, companyId);
    const age = getCacheAge(table, companyId);
    summary[table] = {
      rows: data ? data.length : 0,
      cachedAt: age
    };
  });
  return summary;
}
